import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BreadCrumb } from '@nationwide/internet-servicing-angular-components';
import { PersonalConstants } from 'app/common/personalConstants';
import { ROUTING } from 'app/common/routeConstants';
import { REPAIR_SHOP_API_ENDPOINTS } from 'app/shared/constants/claims.constants';
import { AddressDTO, EntegralSelectRepairFacilityRequest, UpdateServiceOfferingData, VehicleDTO } from 'app/shared/models/claims-efnol-request-data.model';
import { OysRepairShop } from 'app/shared/models/oys.model';
import { ClaimsFnolService } from 'app/shared/services/claims-fnol.service';
import { ClaimsHelperService } from 'app/shared/services/claims-helper.service';
import { ClaimsTrackerService } from 'app/shared/services/claims-tracker.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';
import { environment } from 'environments/environment';
import { lastValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { VehicleLocationAddressFormComponent } from '../vehicle-location-address-form/vehicle-location-address-form.component';

interface VehicleLocationFormValue {
    country: string;
    state: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    zipCode: string;
    locationDescription: string;
}

@Component({
    selector: 'is-oys-tracker-shop-confirm',
    templateUrl: './oys-tracker-shop-confirm.component.html',
    styleUrls: ['./oys-tracker-shop-confirm.component.scss'],
    standalone: false
})
export class OysTrackerShopConfirmComponent implements OnInit, OnDestroy {
    @ViewChild(VehicleLocationAddressFormComponent, { static: false }) vehicleLocationFormChild: VehicleLocationAddressFormComponent;
    breadCrumbs!: BreadCrumb[];
    homeIconLink = environment.NATIONWIDE.HOME;
    currentPage = 'Confirm repair shop';

    selectedShop?: OysRepairShop;
    vehicleInfo?: VehicleDTO;
    homeAddress?: AddressDTO;
    confirmForm!: FormGroup;
    isLoading = true;
    isOtherVehicleLocation = false;
    errorMessage!: string;
    childFormValues: any;
    isChildFormInvalid: boolean = false;
    public vehicleLocationFormValue: VehicleLocationFormValue;

    private ngUnsubscribe = new Subject<void>();

    // Data from session storage
    incidentId!: string;
    oysPublicId!: string;
    towingPublicId!: string;
    towingEligible!: boolean;
    claimNumber!: string;
    policyNumber!: string;

    private entegralClaimId = '';

    constructor(
        private formBuilder: FormBuilder,
        private claimsTrackerService: ClaimsTrackerService,
        private claimsFnolService: ClaimsFnolService,
        private sessionService: SessionService,
        private claimsHelperService: ClaimsHelperService,
        private logger: LoggerService,
        private router: Router
    ) {}

    @HostListener('window:beforeunload', ['$event'])
    unloadHandler(event: any): any {
        this.logger.logCurrentContents();
    }

    ngOnInit(): void {
        this.setBreadCrumbs();
        this.logger.setPageName('oysTrackerShopConfirm');
        this.claimNumber = this.claimsHelperService.getClaimId();

        // Read from session storage
        this.selectedShop = JSON.parse(sessionStorage.getItem('oysTrackerSelectedShop') || '{}');
        this.incidentId = sessionStorage.getItem('trackerVehicleIncidentId') || '';
        this.oysPublicId = sessionStorage.getItem('oysTrackerOysPublicId') || '';
        this.towingPublicId = sessionStorage.getItem('oysTrackerTowingPublicId') || '';
        this.towingEligible = sessionStorage.getItem('oysTrackerTowingEligible') === 'true';
        this.policyNumber = this.sessionService.getPolicyNumber() || '';

        this.logger.info('OYS Shop Confirm Loaded', {
            selectedShop: this.selectedShop,
            incidentId: this.incidentId,
            claimNumber: this.claimNumber
        });

        this.initializeForm();

        this.getVehicleInfo();
    }

    initializeForm(): void {
        this.confirmForm = this.formBuilder.group({
            vehicleLocation: ['', Validators.required],
            towing: [''],
            towingAuth: [false]
        });

        this.vehicleLocationCtrl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
            if ((value === 'Home' || value === 'Other') && this.towingEligible) {
                this.towingCtrl.setValidators([Validators.required]);
                this.towingCtrl.updateValueAndValidity();
            } else {
                this.towingCtrl.clearValidators();
                this.towingCtrl.updateValueAndValidity();
            }

            if (value === 'Other') {
                this.isOtherVehicleLocation = true;
            } else {
                this.isOtherVehicleLocation = false;
            }
        });

        this.towingCtrl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
            if (value === 'Yes') {
                this.towingAuthCtrl.setValidators([Validators.requiredTrue]);
                this.towingAuthCtrl.updateValueAndValidity();
            } else {
                this.towingAuthCtrl.clearValidators();
                this.towingAuthCtrl.updateValueAndValidity();
            }
        });
    }

    getVehicleInfo(): void {
        this.claimsTrackerService.getClaimVehicleIncidents(this.claimNumber).subscribe(
            (response: any) => {
                const incident = response.incidents?.find((inc: any) => inc.publicID === this.incidentId);
                if (incident && incident.vehicle) {
                    this.entegralClaimId = incident.nw_ExternalClaimId || '';
                    this.homeAddress = incident.incidentOwner.primaryAddress;
                    if (this.homeAddress?.country === 'US') {
                        this.homeAddress.country = 'United States';
                    }
                    this.vehicleInfo = {
                        year: incident.vehicle.year,
                        make: incident.vehicle.make,
                        model: incident.vehicle.model
                    };
                } else {
                    this.logger.info('OYS Tracker Shop Confirm: No matching incident found', { incidentId: this.incidentId });
                }
                this.isLoading = false;
            },
            (error: any) => {
                this.logger.error('Failed to fetch vehicle incidents', error);
                this.errorMessage = 'Unable to load vehicle information. Please try again.';
                this.isLoading = false;
            }
        );
    }

    setBreadCrumbs(): void {
        const policyNumber = this.sessionService.getPolicyNumber();
        this.breadCrumbs = [
            {
                name: 'All accounts',
                link: `${environment.CLAIMS_OLD_APP.ALL_ACCOUNTS}${PersonalConstants.CLAIMS_OLD_URLS.ACCOUNT_SUMMARY_PAGE}`
            },
            {
                name: 'Auto claims',
                link: `${environment.CLAIMS_NEW_APP.BASE_URL}${PersonalConstants.CLAIMS_URLS.CLAIM_LIST_PAGE}?policyNumber=${encodeURIComponent(policyNumber)}`
            },
            {
                name: 'Track your claim',
                link: `${ROUTING.AUTO.BASE_PATH}/${ROUTING.AUTO.CHILD_PATH_TRACKER}`
            }
        ];
    }

    getDirections(): void {
        if (this.selectedShop && this.selectedShop.address) {
            const dirUrl = `https://www.google.com/maps/place/${this.selectedShop.address}+${this.selectedShop.city}+${this.selectedShop.state}+${this.selectedShop.country || 'US'}+${this.selectedShop.zipCode}`;
            window.open(dirUrl, '_blank');
        }
    }

    onTowingAuthChange(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.towingAuthCtrl.setValue(checked);
        this.towingAuthCtrl.markAsTouched();
    }

    public onVehicleLocationFormValid(formGroup: UntypedFormGroup): void {
        this.vehicleLocationFormValue = formGroup.value;
        this.isChildFormInvalid = formGroup.untouched || formGroup.invalid;
    }

    async onSubmit(): Promise<void> {
        this.confirmForm.markAllAsTouched();

        if (this.isOtherVehicleLocation && this.vehicleLocationFormChild) {
            this.vehicleLocationFormChild.triggerValidationForAllFields();
        }

        if (!this.confirmForm.valid || !this.selectedShop) {
            return;
        }

        if (this.isOtherVehicleLocation) {
            this.childFormValues = this.vehicleLocationFormChild.vehicleLocationFormSubmit();

            if (!this.childFormValues) {
                return;
            }
        }

        this.isLoading = true;
        this.errorMessage = '';

        try {
            const oysRequest: UpdateServiceOfferingData = {
                type: 'oysAuto',
                serviceProvider: {
                    ...this.selectedShop,
                    subtype: 'NW_BRRS',
                    shopID: this.selectedShop.nationwideShopID
                }
            };

            await lastValueFrom(
                this.claimsFnolService.updateServiceOfferings(
                    this.claimNumber,
                    this.incidentId,
                    this.oysPublicId,
                    oysRequest
                )
            );

            this.logger.info('OYS shop selection updated successfully');

            await this.sendShopSelectionToEntegral();

            if (this.towingCtrl.value === 'Yes' && this.towingEligible) {
                const vehicleLocation = this.vehicleLocationCtrl.value;
                const pickupAddress: any = {};

                if (vehicleLocation === 'Home' && this.homeAddress) {
                    pickupAddress.City = this.homeAddress.city;
                    pickupAddress.State = this.homeAddress.state;
                    pickupAddress.Country = this.homeAddress.country || 'US';
                    pickupAddress.AddressLine1 = this.homeAddress.addressLine1;
                    pickupAddress.ZipCode = this.homeAddress.postalCode;
                } else if (vehicleLocation === 'Other') {
                    pickupAddress.City = this.childFormValues['city'];
                    pickupAddress.State = this.childFormValues['state'];
                    pickupAddress.Country = this.childFormValues['country'];
                    pickupAddress.AddressLine1 = this.childFormValues['addressLine1'];
                    pickupAddress.AddressLine2 = this.childFormValues['addressLine2'];
                    pickupAddress.ZipCode = this.childFormValues['zipCode'];
                    pickupAddress.Description = this.childFormValues['locationDescription'];
                } else {
                    pickupAddress.Country = 'US';
                }
                const destinationAddress = this.buildDestinationAddress();

                const towingRequest: UpdateServiceOfferingData = {
                    type: 'secondaryTow',
                    PermissionToTow: true,
                    PickupAddress: pickupAddress,
                    PickupAddressDescription: vehicleLocation === 'Home' ? 'Home' : vehicleLocation,
                    DestinationAddress: destinationAddress,
                    DestinationAddressDescription: 'OYS Repair Shop',
                    DestinationContactPoint: this.formatPhoneNumber(this.selectedShop.phoneNumber)
                };

                await lastValueFrom(
                    this.claimsFnolService.updateServiceOfferings(
                        this.claimNumber,
                        this.incidentId,
                        this.towingPublicId,
                        towingRequest
                    )
                );

                this.logger.info('Towing selection updated successfully');
            }

            const queryParam = this.towingCtrl.value === 'Yes' ?
                ROUTING.QUERY.FROM_OYS_REVIEW_TOWING :
                ROUTING.QUERY.FROM_OYS_REVIEW;

            this.sessionService.removeSessionItem('REPAIRS_DATA');
            this.router.navigate(
                [ROUTING.AUTO.BASE_PATH, ROUTING.AUTO.CHILD_PATH_TRACKER],
                { queryParams: { from: queryParam } }
            );
        } catch (error: any) {
            this.isLoading = false;
            this.errorMessage = 'An error occurred while updating your shop selection. Please try again.';
            this.logger.error('Error updating service offerings', error);
        }
    }

    private async sendShopSelectionToEntegral(): Promise<void> {
        const companyId = this.selectedShop?.companyId || '';

        if (!this.entegralClaimId || !companyId) {
            this.logger.info('Skipping Entegral shop selection - missing identifiers');
            return;
        }

        const selectRequest: EntegralSelectRepairFacilityRequest = {
            companyId: companyId,
            clientNetworkKey: REPAIR_SHOP_API_ENDPOINTS.ENTEGRAL_DEFAULT_CLIENT_NETWORK_KEY,
            claimId: this.entegralClaimId,
            interests: []
        };

        try {
            const response = await lastValueFrom(this.claimsFnolService.searchEntegralRepairFacilities(selectRequest));
            this.logger.info('Entegral shop selection sent', response);
        } catch (error: any) {
            this.logger.error('Entegral shop selection failed', error);
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    onCancel(): void {
        this.logger.info('OYS Tracker Shop Confirm: Cancel clicked');
        this.router.navigate([ROUTING.AUTO.BASE_PATH, ROUTING.AUTO.CHILD_PATH_TRACKER]);
    }

    onBack(): void {
        this.logger.info('OYS Tracker Shop Confirm: Back clicked');
        this.router.navigate([
            ROUTING.AUTO.BASE_PATH,
            ROUTING.AUTO.CHILD_PATH_TRACKER,
            ROUTING.AUTO.CHILD_PATH_OYS_SHOP_SELECT
        ]);
    }

    get showTowingQuestion(): boolean {
        return (this.vehicleLocationCtrl.value === 'Home' || this.vehicleLocationCtrl.value === 'Other') && this.towingEligible;
    }

    get showTowingAuth(): boolean {
        return this.towingCtrl.value === 'Yes' && this.showTowingQuestion;
    }

    get showHomeAddress(): boolean {
        return this.vehicleLocationCtrl.value === 'Home';
    }

    get vehicleLocationError(): string {
        return this.vehicleLocationCtrl.invalid && this.vehicleLocationCtrl.touched ? 'Vehicle location is required' : '';
    }

    get towingError(): string {
        return this.towingCtrl.invalid && this.towingCtrl.touched ? 'Towing preference is required' : '';
    }

    get towingAuthError(): string {
        return this.towingAuthCtrl.invalid && this.towingAuthCtrl.touched ? 'Authorization is required' : '';
    }

    private get vehicleLocationCtrl(): AbstractControl {
        return this.confirmForm.controls['vehicleLocation'];
    }

    private get towingCtrl(): AbstractControl {
        return this.confirmForm.controls['towing'];
    }

    private get towingAuthCtrl(): AbstractControl {
        return this.confirmForm.controls['towingAuth'];
    }

    private buildDestinationAddress(): AddressDTO {
        const addressLine1 = this.selectedShop?.address || '';
        const city = this.selectedShop?.city || '';
        const state = this.selectedShop?.state || '';
        const postalCode = this.selectedShop?.zipCode || '';

        return {
            addressLine1,
            city,
            country: 'US',
            description: 'OYS Repair Shop',
            postalCode,
            state
        };
    }

    private formatPhoneNumber(phone?: string): string {
        if (!phone) {
            return '';
        }

        const digits = phone.replace(/\D/g, '');
        const normalized = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

        if (normalized.length !== 10) {
            return phone;
        }

        return `${normalized.slice(0, 3)}-${normalized.slice(3, 6)}-${normalized.slice(6, 10)}`;
    }
}
